import { useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { PageHeader } from '../../components/common/PageHeader';
import { PermissionGate } from '../../components/auth/PermissionGate';
import { SelectField } from '../../components/fields/SelectField';
import { useAuthStore } from '../../store/auth.store';
import { dashboardApi, errorMessage, type TemplateDto } from './dashboardApi';
import { LayoutEditorDialog } from './LayoutEditorDialog';

// Shared dashboard layouts and who gets them. Each tab and button follows the viewer's own grant (create / manage /
// assign); the server enforces every one of those independently. A template only arranges widgets - it never grants the
// data behind them, so assigning one cannot give anybody access they do not already have.
export function DashboardTemplatesPage() {
  const has = useAuthStore((s) => s.hasPermission);
  const canTemplates = has('dashboard:template_create') || has('dashboard:template_manage') || has('dashboard:assignment_manage');
  const canAssign = has('dashboard:assignment_manage');
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <PageHeader title="Dashboard Templates" subtitle="Shared dashboard layouts, and which role or person gets each" />
      <Alert severity="info" sx={{ mb: 2 }}>
        A person sees: their own saved layout (if they may customize) → a template assigned to them → a template assigned to their role → the default.
        Whatever the layout says, each widget still shows only if that person has the permission for its data.
      </Alert>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Templates" disabled={!canTemplates} />
        <Tab label="Assignments" disabled={!canAssign} />
      </Tabs>
      {tab === 0 && canTemplates && <TemplatesTab />}
      {tab === 1 && canAssign && <AssignmentsTab />}
    </Box>
  );
}

function TemplatesTab() {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<TemplateDto | 'new' | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [deleting, setDeleting] = useState<TemplateDto | null>(null);

  const templates = useQuery({ queryKey: ['dashboard-templates'], queryFn: dashboardApi.templates });
  const catalog = useQuery({ queryKey: ['dashboard-catalog'], queryFn: dashboardApi.catalog });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dashboard-templates'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); };
  const fail = (e: unknown) => enqueueSnackbar(errorMessage(e), { variant: 'error' });

  const save = useMutation({
    mutationFn: (widgets: Parameters<typeof dashboardApi.createTemplate>[0]['widgets']) => editing === 'new'
      ? dashboardApi.createTemplate({ name, description, widgets })
      : dashboardApi.updateTemplate((editing as TemplateDto).id, { name, description, isActive, widgets }),
    onSuccess: () => { enqueueSnackbar('Template saved', { variant: 'success' }); setEditing(null); refresh(); },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => dashboardApi.deleteTemplate(id),
    onSuccess: () => { enqueueSnackbar('Template deleted', { variant: 'success' }); setDeleting(null); refresh(); qc.invalidateQueries({ queryKey: ['dashboard-assignments'] }); },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (t: TemplateDto) => dashboardApi.updateTemplate(t.id, { isActive: !t.isActive }),
    onSuccess: refresh, onError: fail,
  });

  const open = (t: TemplateDto | 'new') => {
    setEditing(t);
    setName(t === 'new' ? '' : t.name); setDescription(t === 'new' ? '' : t.description ?? ''); setIsActive(t === 'new' ? true : t.isActive);
  };
  const locked = editing && editing !== 'new' ? Object.fromEntries(editing.widgets.map((w) => [w.id, w.title])) : {};

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
        <PermissionGate permission="dashboard:template_create">
          <Button variant="contained" startIcon={<Add />} onClick={() => open('new')} disabled={!catalog.data}>New template</Button>
        </PermissionGate>
      </Box>
      <Table size="small">
        <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Widgets</TableCell><TableCell>Assigned to</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
        <TableBody>
          {(templates.data ?? []).map((t) => (
            <TableRow key={t.id} hover>
              <TableCell><Typography fontWeight={600}>{t.name}</Typography><Typography variant="caption" color="text.secondary">{t.description}</Typography></TableCell>
              <TableCell>{t.widgets.map((w) => <Chip key={w.id} size="small" label={w.title} sx={{ mr: 0.5, mb: 0.5 }} />)}</TableCell>
              <TableCell>{t.assignmentCount}</TableCell>
              <TableCell><Chip size="small" color={t.isActive ? 'success' : 'default'} label={t.isActive ? 'Active' : 'Inactive'} /></TableCell>
              <TableCell align="right">
                <PermissionGate permission="dashboard:template_manage">
                  <Tooltip title={t.isActive ? 'Deactivate' : 'Activate'}><Switch size="small" checked={t.isActive} onChange={() => toggle.mutate(t)} inputProps={{ 'aria-label': `${t.isActive ? 'Deactivate' : 'Activate'} ${t.name}` }} /></Tooltip>
                  <IconButton aria-label={`Edit ${t.name}`} onClick={() => open(t)} disabled={!catalog.data}><Edit fontSize="small" /></IconButton>
                  <IconButton aria-label={`Delete ${t.name}`} onClick={() => setDeleting(t)}><Delete fontSize="small" /></IconButton>
                </PermissionGate>
              </TableCell>
            </TableRow>
          ))}
          {templates.data?.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No templates yet.</TableCell></TableRow>}
        </TableBody>
      </Table>

      {editing && catalog.data && (
        <LayoutEditorDialog
          open
          title={editing === 'new' ? 'New dashboard template' : `Edit template: ${editing.name}`}
          catalog={catalog.data}
          initialWidgets={editing === 'new' ? [] : editing.layout.widgets}
          lockedTitles={locked}
          saveLabel="Save template"
          saving={save.isPending}
          canSave={name.trim().length > 0}
          onSave={(w) => save.mutate(w)}
          onClose={() => setEditing(null)}
          header={(
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, mb: 2 }}>
              <TextField size="small" label="Template name" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} InputLabelProps={{ shrink: true }} required />
              <TextField size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} inputProps={{ maxLength: 300 }} InputLabelProps={{ shrink: true }} />
              {editing !== 'new' && <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label="Active" />}
            </Box>
          )}
        />
      )}

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography>
            “{deleting?.name}” will be deleted{deleting && deleting.assignmentCount > 0 ? ` and removed from ${deleting.assignmentCount} assignment(s); those people fall back to their next layout` : ''}.
            Nobody's access to any data changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => deleting && remove.mutate(deleting.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function AssignmentsTab() {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [targetType, setTargetType] = useState<'ROLE' | 'USER'>('ROLE');
  const [targetId, setTargetId] = useState('');
  const [templateId, setTemplateId] = useState('');

  const assignments = useQuery({ queryKey: ['dashboard-assignments'], queryFn: dashboardApi.assignments });
  const templates = useQuery({ queryKey: ['dashboard-templates'], queryFn: dashboardApi.templates });
  const targets = useQuery({ queryKey: ['dashboard-targets'], queryFn: dashboardApi.targets });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dashboard-assignments'] }); qc.invalidateQueries({ queryKey: ['dashboard-templates'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); };
  const fail = (e: unknown) => enqueueSnackbar(errorMessage(e), { variant: 'error' });

  const assign = useMutation({
    mutationFn: () => dashboardApi.assign({ templateId, ...(targetType === 'ROLE' ? { roleId: targetId } : { companyUserId: targetId }) }),
    onSuccess: () => { enqueueSnackbar('Template assigned', { variant: 'success' }); setTargetId(''); refresh(); },
    onError: fail,
  });
  const unassign = useMutation({ mutationFn: (id: string) => dashboardApi.unassign(id), onSuccess: () => { enqueueSnackbar('Assignment removed', { variant: 'success' }); refresh(); }, onError: fail });

  const usable = (templates.data ?? []).filter((t) => t.isActive);
  const options = targetType === 'ROLE'
    ? (targets.data?.roles ?? []).map((r) => ({ value: r.id, label: r.name }))
    : (targets.data?.members ?? []).map((m) => ({ value: m.id, label: `${m.name} (${m.email})` }));

  return (
    <Card>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr auto' }, p: 2, alignItems: 'start' }}>
        <SelectField size="small" label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} emptyLabel="Choose…" options={usable.map((t) => ({ value: t.id, label: t.name }))} />
        <SelectField size="small" label="Assign to" value={targetType} onChange={(e) => { setTargetType(e.target.value as 'ROLE' | 'USER'); setTargetId(''); }} options={[{ value: 'ROLE', label: 'A role' }, { value: 'USER', label: 'One person' }]} />
        <SelectField size="small" label={targetType === 'ROLE' ? 'Role' : 'Person'} value={targetId} onChange={(e) => setTargetId(e.target.value)} emptyLabel="Choose…" options={options} />
        <Button variant="contained" sx={{ height: 40 }} disabled={!templateId || !targetId || assign.isPending} onClick={() => assign.mutate()}>Assign</Button>
      </Box>
      <Table size="small">
        <TableHead><TableRow><TableCell>Assigned to</TableCell><TableCell>Template</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
        <TableBody>
          {(assignments.data ?? []).map((a) => (
            <TableRow key={a.id} hover>
              <TableCell><Chip size="small" label={a.target.type === 'ROLE' ? 'Role' : 'Person'} sx={{ mr: 1 }} />{a.target.name}{a.target.email ? ` (${a.target.email})` : ''}</TableCell>
              <TableCell>{a.template.name}{!a.template.isActive && <Chip size="small" label="Inactive - not applied" sx={{ ml: 1 }} />}</TableCell>
              <TableCell align="right"><IconButton aria-label={`Remove assignment for ${a.target.name}`} onClick={() => unassign.mutate(a.id)}><Delete fontSize="small" /></IconButton></TableCell>
            </TableRow>
          ))}
          {assignments.data?.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>Nothing assigned: everyone uses their own layout or the default.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}
